import React, { FC, useState, useRef, useCallback } from 'react'
import { TextBox, TextBoxProps } from '@ds/ui'
import { useTimer } from '@ds/react-utils'

/*

    An input that will only report changes every X milliseconds, where X is a number passed as the `delay` prop.
    Useful when changes to a text field will trigger a network request, an expensive render, or any other 
    resource-intensive process. 

    The `delayedOnChange` event is fired immediately for the first keystroke. As the user continues to type,
    the component updates its internal state to record and display the keystrokes in real time. After the
    delay has expired the `delayedOnChange` event may fire again, reporting the text that the input contains
    at that moment. It will only fire if the input has changed from the previously reported value.

*/

export interface DebouncedTextBoxProps extends TextBoxProps {
  delayedOnChange: (searchText: string) => void
  delay: number
}

export const DebouncedTextBox: FC<DebouncedTextBoxProps> = ({
  delayedOnChange,
  delay,
  ...rest
}) => {
  const previousReportedValue = useRef('')
  const [value, setValue] = useState('')
  const { runLater, status } = useTimer()

  const maybeReportChange = useCallback(
    (_value: string) => {
      if (previousReportedValue.current !== _value) {
        previousReportedValue.current = _value
        delayedOnChange(_value)
      }
    },
    [delayedOnChange]
  )

  const debounceChange = useCallback(
    (_value: string) => {
      if (!status.scheduled) {
        maybeReportChange(_value)
      }
      runLater(() => {
        maybeReportChange(_value)
      }, delay)
    },
    [status.scheduled, runLater, delay, maybeReportChange]
  )

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value: _value } = event.target
      setValue(_value)
      debounceChange(_value)
    },
    [debounceChange]
  )

  return <TextBox {...rest} value={value} onChange={handleChange} />
}
